import { useEffect, useState, useRef, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Loader2, Package, Eye, Download, ArrowUpDown, ArrowUp, ArrowDown, CheckCircle, Check, AlertTriangle, HelpCircle, XCircle } from 'lucide-react';
import { purchasingAPI, coreAPI, suppliersAPI, CommodityGroup, CommodityType, Site, CompanyGroup, SupplierProfile, DeliveryAppeal } from '../../../lib/api';
import { toast } from 'sonner';
import DeliveryDetailsModal from '../modals/DeliveryDetailsModal';
import RaiseAppealModal from '../modals/RaiseAppealModal';
import AppealResponseModal from '../modals/AppealResponseModal';
import { AcknowledgeGRNModal, type GRNWorkflowData } from '../GRNWorkflowModals';
import { DeliveryFilterSection } from './DeliveryFilterSection';
import { Pagination } from '../../ui/pagination';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { SectionTourTrigger } from '../../tour/SectionTourTrigger';
import { GoodsReceiptNote } from '../../../lib/api/types/grn-types';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { useAuth } from '../../../contexts/AuthContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { useView } from '../../../contexts/ViewContext';

function formatLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function getThisWeekMonday(): string {
  const today = new Date();
  const daysSinceMonday = (today.getDay() + 6) % 7;
  return formatLocalDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceMonday));
}
function getThisWeekSunday(): string {
  const today = new Date();
  const daysSinceMonday = (today.getDay() + 6) % 7;
  const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceMonday);
  return formatLocalDate(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6));
}

interface AppealsSubTabProps {
  formatDate: (dateString: string | undefined) => string;
  getAppealStatusBadge: (status: string, isCustomerAppeal?: boolean, workflowStatus?: string) => ReactNode;
  onViewGrn: (grnId: number) => void;
  onRespondAppeal: (appealId: number) => void;
  // Bump this counter from the parent (e.g. after an Accept/Reject/Query in
  // AppealResponseModal closes) to force the appeals list and tab counts to
  // refetch — without this, accepted rows lingered with the stale
  // "awaiting_response" badge and Respond button until a hard refresh.
  refreshKey?: number;
}

function AppealsSubTab({ formatDate, getAppealStatusBadge, onViewGrn, onRespondAppeal, refreshKey = 0 }: AppealsSubTabProps) {
  const [appeals, setAppeals] = useState<DeliveryAppeal[]>([]);
  const [appealsLoading, setAppealsLoading] = useState(true);
  const [appealsError, setAppealsError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  // CR-4736: Proper pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // CR-4736 rework: Default to 25 to align with pagination display
  const [appealPageSize, setAppealPageSize] = useState(25);
  const [appealFilters, setAppealFilters] = useState({
    status: '',
    grn_number: '',
    po_number: '',
    part_number: '',
  });
  // CR-4786: tab bar above the table — gives customers a one-click route to
  // appeals awaiting their response, matching the Exceptions-style pattern
  // used on PO Lines. Ticket called out manual scanning as the pain point.
  type AppealTab = 'all' | 'action_required' | 'resolved';
  const [appealTab, setAppealTab] = useState<AppealTab>('all');
  // CR-4651 rework: per-tab counts shown in the labels in brackets, like the
  // PO Lines tabs. Recomputed whenever the user's filter set changes.
  const [appealTabCounts, setAppealTabCounts] = useState<Record<AppealTab, number>>({
    all: 0,
    action_required: 0,
    resolved: 0,
  });

  useEffect(() => {
    // CR-4736: Reset to page 1 when filters change
    setCurrentPage(1);
  }, [appealFilters, appealTab]);

  useEffect(() => {
    fetchAppeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appealFilters, appealTab, currentPage, appealPageSize, refreshKey]);

  // CR-4651 rework P6a: fetch the count for each sub-tab so the labels can
  // show "(N)" without the user having to click into each tab. Only refires
  // when filters change (not on tab switch or pagination) since the counts
  // are filter-scoped, not tab-scoped.
  // refreshKey: also re-runs after the parent forces a refresh (e.g. after
  // accepting an appeal) so the "(N)" tab counts move alongside the row.
  useEffect(() => {
    fetchTabCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appealFilters, refreshKey]);

  const fetchAppeals = async () => {
    try {
      setAppealsLoading(true);
      setAppealsError(null);
      const params: Record<string, string | number> = {
        ordering: '-created_at',
        page_size: appealPageSize,
        page: currentPage,
      };
      // CR-4786: tab filter takes precedence over the per-field status filter
      // when the user is on Action Required or Resolved — those tabs have a
      // specific meaning and would be confusing to override through the
      // dropdown. The dropdown still works inside the All tab.
      if (appealTab === 'action_required') {
        params['status'] = 'awaiting_response';
        params['is_customer_appeal'] = 'false';
      } else if (appealTab === 'resolved') {
        params['status__in'] = 'accepted,rejected';
      } else if (appealFilters.status) {
        params['status'] = appealFilters.status;
      }
      if (appealFilters.grn_number) params['grn__grn_number__icontains'] = appealFilters.grn_number;
      if (appealFilters.po_number) params['grn__po_line__po__po_number__icontains'] = appealFilters.po_number;
      if (appealFilters.part_number) params['grn__po_line__part_number__icontains'] = appealFilters.part_number;
      const response = await purchasingAPI.getDeliveryAppeals(params);
      const data = response.data;
      if (Array.isArray(data)) {
        setAppeals(data);
        setTotalCount(data.length);
      } else if (data && typeof data === 'object' && 'results' in data) {
        const paginated = data as { results: DeliveryAppeal[]; count: number };
        setAppeals(paginated.results);
        setTotalCount(paginated.count);
      }
    } catch (err: unknown) {
      console.error('Error fetching appeals:', err);
      setAppealsError(getApiErrorMessage(err, 'Failed to load appeals'));
    } finally {
      setAppealsLoading(false);
    }
  };

  const clearAppealFilters = () => {
    setAppealFilters({ status: '', grn_number: '', po_number: '', part_number: '' });
  };

  // CR-4651 rework P6a: cheap (page_size=1) parallel calls to read the
  // .count off each tab's filtered queryset.
  const fetchTabCounts = async () => {
    const baseFilters: Record<string, string | number> = {};
    if (appealFilters.grn_number) baseFilters['grn__grn_number__icontains'] = appealFilters.grn_number;
    if (appealFilters.po_number) baseFilters['grn__po_line__po__po_number__icontains'] = appealFilters.po_number;
    if (appealFilters.part_number) baseFilters['grn__po_line__part_number__icontains'] = appealFilters.part_number;

    const extractCount = (data: unknown): number => {
      if (Array.isArray(data)) return data.length;
      if (data && typeof data === 'object' && 'count' in data) {
        return (data as { count: number }).count;
      }
      return 0;
    };

    try {
      const [allRes, actionRes, resolvedRes] = await Promise.all([
        purchasingAPI.getDeliveryAppeals({ ...baseFilters, page_size: 1, page: 1 }),
        purchasingAPI.getDeliveryAppeals({
          ...baseFilters, status: 'awaiting_response', is_customer_appeal: 'false',
          page_size: 1, page: 1,
        }),
        purchasingAPI.getDeliveryAppeals({
          ...baseFilters, status__in: 'accepted,rejected', page_size: 1, page: 1,
        }),
      ]);
      setAppealTabCounts({
        all: extractCount(allRes.data),
        action_required: extractCount(actionRes.data),
        resolved: extractCount(resolvedRes.data),
      });
    } catch (err) {
      console.error('Failed to fetch appeal tab counts:', err);
    }
  };

  const [exporting, setExporting] = useState(false);
  const handleExportAppeals = async () => {
    try {
      setExporting(true);
      const params: Record<string, string | number> = {};
      if (appealFilters.status) params['status'] = appealFilters.status;
      if (appealFilters.grn_number) params['grn__grn_number__icontains'] = appealFilters.grn_number;
      if (appealFilters.po_number) params['grn__po_line__po__po_number__icontains'] = appealFilters.po_number;
      if (appealFilters.part_number) params['grn__po_line__part_number__icontains'] = appealFilters.part_number;
      const response = await purchasingAPI.exportDeliveryAppeals(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `appeals_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Appeals exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export appeals');
    } finally {
      setExporting(false);
    }
  };

  if (appealsLoading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (appealsError) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {appealsError}</p>
            <button onClick={fetchAppeals} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-orange-600" />
            Submitted Appeals
            <span className="text-sm text-muted-foreground font-normal">({totalCount})</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
            <button
              onClick={handleExportAppeals}
              disabled={exporting}
              className="px-3 py-1.5 text-sm rounded-lg border hover:bg-muted transition-colors flex items-center gap-1 disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export
            </button>
          </div>
        </div>
        {showFilters && (
          <div className="grid grid-cols-4 gap-3 mt-3 pt-3 border-t">
            <div>
              <label className="block text-xs font-medium mb-1">Status</label>
              <select
                value={appealFilters.status}
                onChange={(e) => setAppealFilters({ ...appealFilters, status: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border rounded-lg"
              >
                <option value="">All</option>
                <option value="awaiting_response">Awaiting Response</option>
                <option value="accepted">Accepted</option>
                <option value="queried">Queried</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">GRN Number</label>
              <input
                type="text"
                value={appealFilters.grn_number}
                onChange={(e) => setAppealFilters({ ...appealFilters, grn_number: e.target.value })}
                placeholder="Search..."
                className="w-full px-2 py-1.5 text-sm border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">PO Number</label>
              <input
                type="text"
                value={appealFilters.po_number}
                onChange={(e) => setAppealFilters({ ...appealFilters, po_number: e.target.value })}
                placeholder="Search..."
                className="w-full px-2 py-1.5 text-sm border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Part Number</label>
              <input
                type="text"
                value={appealFilters.part_number}
                onChange={(e) => setAppealFilters({ ...appealFilters, part_number: e.target.value })}
                placeholder="Search..."
                className="w-full px-2 py-1.5 text-sm border rounded-lg"
              />
            </div>
            <div className="col-span-4 flex justify-end">
              <button onClick={clearAppealFilters} className="text-xs text-muted-foreground hover:text-foreground underline">
                Clear All
              </button>
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {/* CR-4786: quick-isolate tabs — Action Required surfaces supplier
            appeals awaiting the customer's response so they don't have to
            scan the full list. Resolved hides handled items. */}
        <div className="px-4 pt-2">
          <div className="inline-flex rounded-lg border bg-muted/30 p-1 gap-1">
            {(
              [
                { key: 'all', label: 'All' },
                { key: 'action_required', label: 'Action Required' },
                { key: 'resolved', label: 'Resolved' },
              ] as Array<{ key: AppealTab; label: string }>
            ).map((t) => {
              const isActive = appealTab === t.key;
              const count = appealTabCounts[t.key];
              // CR-4651 rework P6b: highlight outstanding work — the
              // Action Required count goes bold + red when > 0 so it
              // stands out without the user clicking into the tab.
              const isUrgent = t.key === 'action_required' && count > 0;
              return (
                <button
                  key={t.key}
                  onClick={() => setAppealTab(t.key)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                  }`}
                >
                  {t.label}{' '}
                  <span
                    className={
                      isUrgent && !isActive
                        ? 'font-bold text-red-600'
                        : isActive
                          ? 'opacity-90'
                          : 'text-muted-foreground'
                    }
                  >
                    ({count})
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        {appeals.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No delivery appeals found.</p>
            <p className="text-sm mt-2">Appeals are submitted by suppliers to dispute on-time delivery status.</p>
          </div>
        ) : (
          <>
            {/* CR-4736: Top Pagination */}
            <div className="px-4">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCount / appealPageSize)}
                totalCount={totalCount}
                itemsPerPage={appealPageSize}
                onPageChange={(page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                onPageSizeChange={(size) => { setAppealPageSize(size); setCurrentPage(1); }}
                itemLabel="appeals"
              />
            </div>
          {/* CR-4651 rework round 2: give the table a min-width so the
              16 columns don't get squeezed into the viewport (which is
              what hid View + Action on the QA's screen). View + Action
              are pinned to the right edge so they're always visible
              regardless of how far the user has scrolled horizontally,
              mirroring the GRN-Number sticky-left pattern on the main
              Deliveries table. */}
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)]">
            <table className="w-full text-sm min-w-[2200px]">
              <thead className="sticky top-0 z-20">
                <tr className="border-b bg-muted/80 backdrop-blur-sm">
                  <th className="text-left py-3 px-4 font-medium">Appeal #</th>
                  <th className="text-left py-3 px-4 font-medium">GRN Number</th>
                  <th className="text-left py-3 px-4 font-medium">PO Number</th>
                  <th className="text-left py-3 px-4 font-medium">Line #</th>
                  <th className="text-left py-3 px-4 font-medium">Part Number</th>
                  <th className="text-left py-3 px-4 font-medium">Supplier</th>
                  <th className="text-left py-3 px-4 font-medium">Status</th>
                  <th className="text-left py-3 px-4 font-medium">On-Time</th>
                  <th className="text-left py-3 px-4 font-medium">MRP Date</th>
                  <th className="text-left py-3 px-4 font-medium">Review Date</th>
                  <th className="text-left py-3 px-4 font-medium">ODD</th>
                  <th className="text-left py-3 px-4 font-medium">Delivery Date</th>
                  <th className="text-left py-3 px-4 font-medium min-w-[200px]">Supplier Note</th>
                  <th className="text-left py-3 px-4 font-medium">Created</th>
                  {/* CR-4651 rework P7: dedicated View + Action columns
                      matching the PO Lines screen, pinned right so they
                      survive the horizontal scroll. */}
                  <th className="text-left py-3 px-4 font-medium sticky right-[100px] bg-muted/95 backdrop-blur-sm z-30 min-w-[100px]">View</th>
                  <th className="text-left py-3 px-4 font-medium sticky right-0 bg-muted/95 backdrop-blur-sm z-30 min-w-[100px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {appeals.map((appeal) => (
                  <tr key={appeal.id} className="border-b hover:bg-muted/50 transition-colors">
                    <td className="py-3 px-4 font-medium">{appeal.id}</td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => onViewGrn(appeal.grn)}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                      >
                        {appeal.grn_number}
                      </button>
                    </td>
                    <td className="py-3 px-4">{appeal.po_number}</td>
                    <td className="py-3 px-4">{appeal.line_number}</td>
                    <td className="py-3 px-4 font-mono">{appeal.part_number}</td>
                    <td className="py-3 px-4">{appeal.supplier_name || '-'}</td>
                    <td className="py-3 px-4">{getAppealStatusBadge(appeal.status, appeal.is_customer_appeal)}</td>
                    <td className="py-3 px-4">{appeal.on_time_status || '-'}</td>
                    <td className="py-3 px-4">{appeal.grn_mrp_date ? formatDate(appeal.grn_mrp_date) : '-'}</td>
                    <td className="py-3 px-4">{appeal.grn_review_date ? formatDate(appeal.grn_review_date) : '-'}</td>
                    <td className="py-3 px-4">{appeal.grn_original_delivery_date ? formatDate(appeal.grn_original_delivery_date) : '-'}</td>
                    <td className="py-3 px-4">{appeal.grn_receipt_date ? formatDate(appeal.grn_receipt_date) : '-'}</td>
                    <td className="py-3 px-4 max-w-[200px] truncate" title={appeal.supplier_note}>{appeal.supplier_note || '-'}</td>
                    <td className="py-3 px-4">{formatDate(appeal.created_at)}</td>
                    {/* CR-4651 rework round 2: cells mirror the sticky
                        header so View + Action stay pinned to the right
                        edge during horizontal scroll. */}
                    <td className="py-3 px-4 sticky right-[100px] bg-background z-10 min-w-[100px]">
                      <button
                        onClick={() => onViewGrn(appeal.grn)}
                        className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-sm"
                        title="View delivery details"
                      >
                        <Eye className="h-4 w-4" /> View
                      </button>
                    </td>
                    <td className="py-3 px-4 sticky right-0 bg-background z-10 min-w-[100px]">
                      {appeal.status === 'awaiting_response' && !appeal.is_customer_appeal ? (
                        <button
                          onClick={() => onRespondAppeal(appeal.id)}
                          className="px-2 py-1 text-xs rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          Respond
                        </button>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            {/* CR-4736: Bottom Pagination */}
            <div className="px-4">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCount / appealPageSize)}
                totalCount={totalCount}
                itemsPerPage={appealPageSize}
                onPageChange={(page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                onPageSizeChange={(size) => { setAppealPageSize(size); setCurrentPage(1); }}
                itemLabel="appeals"
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function DeliveryTab() {
  const { user } = useAuth();
  const isSupplier = user?.platform === 'SUPPLIER';
  // Schedule-aware tenants: On-Time Status at PO-line / GRN level is
  // undefined because one GRN can span multiple schedules with
  // different receipt-vs-schedule-date verdicts. Per-allocation OTIF
  // lives inside the Delivery modal's Schedule Allocation tab. Hide
  // the column here so the grid doesn't imply a single-value answer.
  const { isFeatureEnabled, isOpenOrdersControlHidden } = useMenuSettings();
  // CR-5884: Open Orders supplier-facing hide-toggles. The Deliveries view
  // is part of the supplier-facing "Open Orders" experience, so the same
  // admin toggles apply here. Customer / Helix viewers always see controls.
  const hideExceptionsForSupplier = isOpenOrdersControlHidden('hide_exceptions', isSupplier);
  const hideActionsForSupplier = isOpenOrdersControlHidden('hide_actions', isSupplier);
  const hideAppealForSupplier = isOpenOrdersControlHidden('hide_otif_override_button', isSupplier);
  const scheduleCollabActive = isFeatureEnabled('po_line_schedules');
  // CR-4665 rework: Honour the active view-context scope so the deliveries
  // list refreshes when the user switches supplier/site/group via the
  // top-right selector. Previously this tab ignored ViewContext entirely
  // and the backend fell back to all accessible POs — Sarah saw Northstar
  // GRNs while the selector said Skybridge.
  const { viewKey, getViewQueryParams } = useView();
  const [searchParams, setSearchParams] = useSearchParams();
  const [grns, setGrns] = useState<GoodsReceiptNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [selectedGrn, setSelectedGrn] = useState<GoodsReceiptNote | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [appealGrn, setAppealGrn] = useState<GoodsReceiptNote | null>(null);
  // CR-4660: Direct acknowledge modal (skip details popup)
  const [acknowledgeGrn, setAcknowledgeGrn] = useState<GoodsReceiptNote | null>(null);
  // CR-4786: Respond-to-appeal modal opened from a GRN row whose latest
  // appeal is awaiting customer response (supplier raised it, customer
  // needs to Accept / Query Supplier / Reject — not re-Acknowledge).
  const [respondAppeal, setRespondAppeal] = useState<DeliveryAppeal | null>(null);
  // Force AppealsSubTab to refetch its rows + tab counts whenever the parent
  // mutates appeal state (Accept / Reject / Query / Cancel). The subtab owns
  // its own data, so a parent fetchData() doesn't reach it — bumping this
  // counter is what flips the row's badge & action column without a hard
  // refresh.
  const [appealsRefreshKey, setAppealsRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);  // CR-3861: Export state
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [sortField, setSortField] = useState('receipt_date');  // Default sort by delivery date
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [pageSize, setPageSize] = useState(50);

  // Read URL search params for deep linking
  const urlSearch = searchParams.get('search');
  const urlGrnNo = searchParams.get('grnno');
  const urlPoNumber = searchParams.get('ponumber');
  const urlLineNumber = searchParams.get('linenumber');
  const urlGrnStatus = searchParams.get('grnstatus');  // e.g. "late", "early"
  const urlAppealStatus = searchParams.get('appealstatus');  // e.g. "pending_review"
  const autoOpenGrn = useRef<string | null>(urlGrnNo);

  // Text input states (for debouncing)
  const [mainSubTab, setMainSubTab] = useState<'deliveries' | 'appeals'>('deliveries');

  const [textInputs, setTextInputs] = useState({
    grnNumber: urlSearch || urlGrnNo || '',
    poNumber: urlPoNumber || '',
    supplier: '',
    partNumber: '',
    lineNumber: urlLineNumber || '',
  });

  // Actual filter state (used for API calls)
  const [filters, setFilters] = useState({
    grnNumber: urlSearch || urlGrnNo || '',
    poNumber: urlPoNumber || '',
    supplier: '',
    partNumber: '',
    lineNumber: urlLineNumber || '',
    inspection_status: '',
    dateFrom: '',
    dateTo: '',
    site: '',
    supplierGroup: '',
    commodityGroup: '',
    commodityType: '',
    appealStatus: urlGrnStatus || urlAppealStatus || '',
    appealFilter: '',
    poLineStatus: '',
    grnStatus: '',
    oddFrom: '',
    oddTo: '',
    strategic: '',
    supplierCode: '',
  });

  const [siteOptions, setSiteOptions] = useState<{ value: string; label: string; group?: number | null }[]>([]);
  const [supplierGroupOptions, setSupplierGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityGroupOptions, setCommodityGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityTypeOptions, setCommodityTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<{ value: string; label: string }[]>([]);

  // Guard: a URL deep-link like ?tab=early_late or ?tab=on_time would
  // otherwise silently land on a now-hidden tab on schedule-aware
  // tenants. Coerce to 'all' when that happens.
  useEffect(() => {
    if (scheduleCollabActive && (activeTab === 'early_late' || activeTab === 'on_time')) {
      setActiveTab('all');
    }
  }, [scheduleCollabActive, activeTab]);

  // CR-4580: No filter persistence — always start fresh
  // Handle URL search params on mount
  useEffect(() => {
    const hasUrlParams = urlSearch || urlGrnNo || urlPoNumber || urlGrnStatus || urlAppealStatus;
    if (hasUrlParams) {
      setShowFilters(true);
      const tabParam = searchParams.get('tab');
      setSearchParams(tabParam ? { tab: tabParam } : {}, { replace: true });
    }
    setInitialLoadComplete(true);

    // Fetch filter options
    Promise.all([
      coreAPI.getAccessibleSites().catch(() => ({ data: [] })),
      purchasingAPI.getCommodityGroups({ enabled: true }).catch(() => ({ data: [] })),
      purchasingAPI.getCommodityTypes({ enabled: true }).catch(() => ({ data: [] })),
      coreAPI.getCompanyGroups({ page_size: 200, company_type: 'supplier' }).catch(() => ({ data: { results: [] } })),
      suppliersAPI.getProfiles({ page_size: 1000, is_active: true }).catch(() => ({ data: { results: [] } })),
    ]).then(([sitesRes, groupsRes, typesRes, companyGroupsRes, suppliersRes]) => {
      const sites = (Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data as unknown as { results: Site[] }).results || []) as Site[];
      setSiteOptions(sites.map((s) => ({ value: String(s.id), label: s.name, group: s.group_id ?? null })));
      const groups = Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data as unknown as { results: CommodityGroup[] }).results || [];
      setCommodityGroupOptions(groups.filter((g: CommodityGroup) => g.enabled).map((g: CommodityGroup) => ({ value: String(g.id), label: g.name })));
      const types = Array.isArray(typesRes.data) ? typesRes.data : (typesRes.data as unknown as { results: CommodityType[] }).results || [];
      setCommodityTypeOptions(types.filter((t: CommodityType) => t.enabled).map((t: CommodityType) => ({ value: String(t.id), label: t.name })));
      const companyGroups = (companyGroupsRes.data as unknown as { results: CompanyGroup[] }).results || [];
      setSupplierGroupOptions(companyGroups.map((g: CompanyGroup) => ({ value: String(g.id), label: g.name })));
      const suppliers = (Array.isArray(suppliersRes.data) ? suppliersRes.data : (suppliersRes.data as unknown as { results: SupplierProfile[] }).results || []) as SupplierProfile[];
      setSupplierOptions(suppliers.map((s) => ({ value: String(s.company), label: s.company_name })));
    });
  }, []);

  // Debounce text inputs
  useEffect(() => {
    if (!initialLoadComplete) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        grnNumber: textInputs.grnNumber,
        poNumber: textInputs.poNumber,
        supplier: textInputs.supplier,
        partNumber: textInputs.partNumber,
        lineNumber: textInputs.lineNumber,
      }));
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [textInputs, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete) {
      fetchData();
    }
    // CR-4665 rework: viewKey changes when the user switches scope via
    // the top-right selector — re-fetch so deliveries reflect the new
    // company/site/group instead of the previous one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, activeTab, initialLoadComplete, sortField, sortDirection, pageSize, viewKey]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build ordering parameter based on sort state
      const orderingPrefix = sortDirection === 'desc' ? '-' : '';
      const queryParams: Record<string, string | number | boolean> = {
        ordering: `${orderingPrefix}${sortField}`,
        page: currentPage,
        page_size: pageSize,
      };

      // CR-4665 rework: Pass the active view-context scope to the API so
      // the backend knows which supplier/site/group to filter to. Without
      // this, get_accessible_po_ids returns ALL accessible POs and the
      // selector becomes a no-op.
      const viewParams = getViewQueryParams();
      if (viewParams['group']) queryParams['group'] = viewParams['group'] as number;
      if (viewParams['company']) queryParams['company'] = viewParams['company'] as number;
      if (viewParams['site']) queryParams['site'] = viewParams['site'] as number;

      // Individual field filters using Django filter syntax
      if (filters.grnNumber) {
        queryParams['grn_number__icontains'] = filters.grnNumber;
      }

      if (filters.poNumber) {
        queryParams['po_line__po__po_number__icontains'] = filters.poNumber;
      }

      if (filters.supplier) {
        queryParams['po_line__po__supplier'] = filters.supplier;
      }

      if (filters.partNumber) {
        queryParams['po_line__part_number__icontains'] = filters.partNumber;
      }

      // Apply inspection status filter from dropdown
      if (filters.inspection_status) {
        queryParams['inspection_status'] = filters.inspection_status;
      }
      // Apply on-time status filter from tab
      if (activeTab === 'on_time') {
        queryParams['on_time_status'] = 'on_time';
      } else if (activeTab === 'early_late') {
        queryParams['on_time_status__in'] = 'early,late';
      } else if (activeTab === 'exceptions') {
        // CR-4651: Exceptions = Early/Late GRNs still needing action
        queryParams['on_time_status__in'] = 'early,late';
        queryParams['workflow_status'] = 'pending_review';
      }

      // Apply date filters (panel inputs win; 'delivered_this_week' tab
      // supplies the Mon→Sun range only when the user hasn't entered
      // their own dates).
      if (filters.dateFrom) {
        queryParams['receipt_date__gte'] = filters.dateFrom;
      } else if (activeTab === 'delivered_this_week') {
        queryParams['receipt_date__gte'] = getThisWeekMonday();
      }
      if (filters.dateTo) {
        queryParams['receipt_date__lte'] = filters.dateTo;
      } else if (activeTab === 'delivered_this_week') {
        queryParams['receipt_date__lte'] = getThisWeekSunday();
      }

      if (filters.lineNumber) {
        queryParams['po_line__line_number'] = filters.lineNumber;
      }
      if (filters.site) {
        queryParams['po_line__po__customer'] = filters.site;
      }
      if (filters.supplierGroup) {
        queryParams['po_line__po__supplier__group'] = filters.supplierGroup;
      }
      if (filters.commodityGroup) {
        queryParams['po_line__commodity_group'] = filters.commodityGroup;
      }
      if (filters.commodityType) {
        queryParams['po_line__commodity_type'] = filters.commodityType;
      }
      if (filters.poLineStatus === 'open') {
        queryParams['po_line__status__in'] = 'pending,partially_received';
      } else if (filters.poLineStatus === 'closed') {
        queryParams['po_line__status__in'] = 'received,cancelled';
      }
      if (filters.grnStatus === 'early_late') {
        queryParams['on_time_status__in'] = 'early,late';
      } else if (filters.grnStatus) {
        queryParams['on_time_status'] = filters.grnStatus;
      }
      if (filters.oddFrom) {
        queryParams['po_line__delivery_date__gte'] = filters.oddFrom;
      }
      if (filters.oddTo) {
        queryParams['po_line__delivery_date__lte'] = filters.oddTo;
      }
      if (filters.appealStatus) {
        queryParams['workflow_status'] = filters.appealStatus;
      }
      if (filters.appealFilter === 'no_appeal') {
        queryParams['appeal_count'] = 0;
      } else if (filters.appealFilter === 'appealed') {
        queryParams['appeal_count__gt'] = 0;
      }
      if (filters.supplierCode) {
        queryParams['po_line__supplier_code__icontains'] = filters.supplierCode;
      }

      const response = await purchasingAPI.getGRNs(queryParams);

      setGrns(response.data.results);
      setTotalCount(response.data.count);
      setTotalPages(Math.ceil(response.data.count / pageSize));

      // Auto-open modal for URL deep-linked GRN
      if (autoOpenGrn.current && response.data.results.length > 0) {
        const match = response.data.results.find(
          (g: GoodsReceiptNote) => g.grn_number === autoOpenGrn.current
        );
        if (match) {
          setSelectedGrn(match);
          setShowModal(true);
        }
        autoOpenGrn.current = null;
      }

      // Fetch tab counts in background (without tab filter applied)
      // CR-4564: Always use page 1 for count queries to avoid "Invalid page" errors
      const countParams: Record<string, string | number | boolean> = { ...queryParams, page: 1, page_size: 1 };
      delete countParams['on_time_status'];
      delete countParams['on_time_status__in'];
      // Drop any receipt_date narrowing applied by the tab so the
      // 'delivered_this_week' count reflects the full week, not the
      // currently-active tab's sub-range.
      const tabOriginatedDateFilter = !filters.dateFrom && !filters.dateTo;
      if (tabOriginatedDateFilter) {
        delete countParams['receipt_date__gte'];
        delete countParams['receipt_date__lte'];
      }
      const thisWeekCountParams = {
        ...countParams,
        receipt_date__gte: filters.dateFrom || getThisWeekMonday(),
        receipt_date__lte: filters.dateTo || getThisWeekSunday(),
      };
      const [allResp, onTimeResp, earlyLateResp, exceptionsResp, deliveredThisWeekResp] = await Promise.all([
        purchasingAPI.getGRNs({ ...countParams }),
        purchasingAPI.getGRNs({ ...countParams, on_time_status: 'on_time' }),
        purchasingAPI.getGRNs({ ...countParams, on_time_status__in: 'early,late' }),
        purchasingAPI.getGRNs({ ...countParams, on_time_status__in: 'early,late', workflow_status: 'pending_review' }),
        purchasingAPI.getGRNs(thisWeekCountParams),
      ]);
      setTabCounts({
        all: allResp.data.count,
        on_time: onTimeResp.data.count,
        early_late: earlyLateResp.data.count,
        exceptions: exceptionsResp.data.count,
        delivered_this_week: deliveredThisWeekResp.data.count,
      });
    } catch (err: unknown) {
      console.error('Error fetching GRN data:', err);
      setError(getApiErrorMessage(err, 'Failed to load GRN data'));
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setTextInputs({
      grnNumber: '',
      poNumber: '',
      supplier: '',
      partNumber: '',
      lineNumber: '',
    });
    setFilters({
      grnNumber: '',
      poNumber: '',
      supplier: '',
      partNumber: '',
      lineNumber: '',
      inspection_status: '',
      dateFrom: '',
      dateTo: '',
      site: '',
      supplierGroup: '',
      commodityGroup: '',
      commodityType: '',
      appealStatus: '',
      appealFilter: '',
      poLineStatus: '',
      grnStatus: '',
      oddFrom: '',
      oddTo: '',
      strategic: '',
      supplierCode: '',
    });
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleFilterChange = (key: string, value: string) => {
    // Text inputs: update textInputs state (will be debounced)
    if (['grnNumber', 'poNumber', 'supplier', 'partNumber', 'lineNumber'].includes(key)) {
      setTextInputs((prev) => ({ ...prev, [key]: value }));
    } else {
      // Dropdowns and dates: update filters directly (instant) — use functional update
      // to avoid stale state when multiple updates happen in sequence
      if (key === 'commodityGroup') {
        setFilters((prev) => ({ ...prev, [key]: value, commodityType: '' }));
      } else {
        setFilters((prev) => ({ ...prev, [key]: value }));
      }
    }
    setCurrentPage(1);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleViewDetails = (grn: GoodsReceiptNote) => {
    setSelectedGrn(grn);
    setShowModal(true);
  };

  // CR-4786: Open the Respond-to-Appeal modal for a GRN whose latest
  // appeal is awaiting customer response. The GRN row only carries a
  // thin latest_appeal stub (id/status/sequence); fetch the full appeal
  // so the modal can render claimed values, supplier note, etc.
  const openAppealResponseForGrn = async (grn: GoodsReceiptNote) => {
    const appealId = grn.latest_appeal?.id;
    if (!appealId) {
      toast.error('No appeal to respond to on this delivery');
      return;
    }
    try {
      const res = await purchasingAPI.getDeliveryAppeal(appealId);
      setRespondAppeal(res.data);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load appeal details'));
    }
  };

  // CR-4660: Direct acknowledge handler (skips DeliveryDetailsModal).
  // CR-4601 round 5: patch the row from the POST response immediately so
  // the Action + Appeal Status columns flip to "Acknowledged" without
  // waiting for the (unawaited) fetchData refetch — Sarah's QA hit a
  // window where the success toast fired but the buttons lingered
  // because the table re-rendered from stale state before the GET
  // returned. The endpoint already returns the updated GRN.
  const handleAcknowledgeSubmit = async (workflowData: GRNWorkflowData) => {
    const res = await purchasingAPI.acknowledgeGRN(workflowData.grnId, { comments: workflowData.comments });
    const updated = res.data?.data;
    if (updated && typeof updated === 'object' && 'id' in updated) {
      setGrns(prev => prev.map(g => g.id === updated.id ? { ...g, ...updated } : g));
    }
    toast.success('GRN acknowledged successfully');
    fetchData();
  };

  // CR-4869/4870: Supplier cancels their own active appeal from the
  // deliveries action column. The backend cancel endpoint only accepts
  // the creator's company (widened to the creator's whole group in
  // CR-4870 to match group-wide appeal visibility); unexpected
  // failures surface via toast.
  const handleCancelAppeal = async (grn: GoodsReceiptNote, e: React.MouseEvent) => {
    e.stopPropagation();
    const appealId = grn.latest_appeal?.id;
    if (!appealId) return;
    if (!window.confirm('Cancel this appeal? This action cannot be undone.')) return;
    try {
      await purchasingAPI.cancelDeliveryAppeal(appealId);
      toast.success('Appeal cancelled');
      fetchData();
      setAppealsRefreshKey(k => k + 1);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to cancel appeal'));
    }
  };

  // Handle column sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  // Sortable header component
  const SortableHeader = ({ field, label, className = '' }: { field: string; label: string; className?: string }) => (
    <th
      className={`py-3 px-4 font-medium cursor-pointer hover:bg-muted/50 transition-colors select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {sortField === field ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5 text-primary" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 text-primary" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50" />
        )}
      </div>
    </th>
  );

  // CR-3861: Export deliveries to CSV. Must mirror the list's filter
  // building exactly — the prior implementation only passed a handful
  // of filters AND incorrectly mapped `activeTab` to
  // `inspection_status`, which caused a 400 on every non-All tab
  // (Exceptions / Early-Late / On Time / Delivered This Week) and
  // dumped the wrong rows when it did succeed.
  const handleExport = async () => {
    try {
      setExporting(true);
      const queryParams: Record<string, string | number | boolean> = {};

      // Text filters
      if (filters.grnNumber) queryParams['grn_number__icontains'] = filters.grnNumber;
      if (filters.poNumber) queryParams['po_line__po__po_number__icontains'] = filters.poNumber;
      if (filters.supplier) queryParams['po_line__po__supplier'] = filters.supplier;
      if (filters.partNumber) queryParams['po_line__part_number__icontains'] = filters.partNumber;
      if (filters.lineNumber) queryParams['po_line__line_number'] = filters.lineNumber;
      if (filters.supplierCode) queryParams['po_line__po__supplier__entity_code'] = filters.supplierCode;

      // Dropdown filters
      if (filters.site) queryParams['po_line__po__customer'] = filters.site;
      if (filters.supplierGroup) queryParams['po_line__po__supplier__group'] = filters.supplierGroup;
      if (filters.commodityGroup) queryParams['po_line__commodity_group'] = filters.commodityGroup;
      if (filters.commodityType) queryParams['po_line__commodity_type'] = filters.commodityType;

      if (filters.poLineStatus === 'open') queryParams['po_line__status__in'] = 'pending,partially_received';
      else if (filters.poLineStatus === 'closed') queryParams['po_line__status__in'] = 'received,cancelled';

      // On-Time Status filter (explicit panel value wins over tab)
      if (filters.grnStatus === 'early_late') queryParams['on_time_status__in'] = 'early,late';
      else if (filters.grnStatus) queryParams['on_time_status'] = filters.grnStatus;
      else if (activeTab === 'on_time') queryParams['on_time_status'] = 'on_time';
      else if (activeTab === 'early_late') queryParams['on_time_status__in'] = 'early,late';
      else if (activeTab === 'exceptions') {
        queryParams['on_time_status__in'] = 'early,late';
        queryParams['workflow_status'] = 'pending_review';
      }

      // Appeal filter
      if (filters.appealFilter === 'appealed') queryParams['has_appeal'] = true;
      else if (filters.appealFilter === 'no_appeal') queryParams['has_appeal'] = false;

      // Receipt date (panel wins, otherwise the 'delivered_this_week' tab supplies Mon→Sun)
      if (filters.dateFrom) queryParams['receipt_date__gte'] = filters.dateFrom;
      else if (activeTab === 'delivered_this_week') queryParams['receipt_date__gte'] = getThisWeekMonday();
      if (filters.dateTo) queryParams['receipt_date__lte'] = filters.dateTo;
      else if (activeTab === 'delivered_this_week') queryParams['receipt_date__lte'] = getThisWeekSunday();

      // ODD (attributed schedule / line delivery date)
      if (filters.oddFrom) queryParams['po_line__delivery_date__gte'] = filters.oddFrom;
      if (filters.oddTo) queryParams['po_line__delivery_date__lte'] = filters.oddTo;

      const response = await purchasingAPI.exportGRNs(queryParams);

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deliveries_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Deliveries exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export deliveries');
    } finally {
      setExporting(false);
    }
  };

  const { formatDate: contextFormatDate } = useFormatters();

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return contextFormatDate(dateString);
  };

  const formatQuantity = (qty: string | number) => {
    const num = typeof qty === 'string' ? parseFloat(qty) : qty;
    return num.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  };

  // CR-4603: Show directional status based on who submitted the appeal
  // CR-4601 rework: When no appeal exists but the GRN has been acknowledged,
  // surface that state in the Appeal Status column ("Acknowledged") instead of
  // the misleading "Not Appealed" label.
  const getAppealStatusBadge = (status: string, isCustomerAppeal?: boolean, workflowStatus?: string, onTimeStatus?: string) => {
    let label = status;
    let cls = 'bg-gray-100 text-gray-500';
    if (status === 'awaiting_response') {
      label = isCustomerAppeal ? 'Awaiting Supplier Response' : 'Awaiting Customer Response';
      cls = 'bg-yellow-100 text-yellow-800';
    } else if (status === 'accepted') { label = 'Accepted'; cls = 'bg-green-100 text-green-800'; }
    else if (status === 'queried') { label = 'Query – Awaiting Supplier Response'; cls = 'bg-blue-100 text-blue-800'; }
    else if (status === 'rejected') { label = 'Rejected'; cls = 'bg-red-100 text-red-800'; }
    else if (status === 'cancelled') { label = 'Appeal Cancelled'; cls = 'bg-gray-200 text-gray-600'; }
    else if (status === 'none' || !status) {
      if (workflowStatus === 'acknowledged') {
        label = 'Acknowledged';
        cls = 'bg-green-100 text-green-800';
      } else if (onTimeStatus === 'on_time') {
        // CR-4651 AC #1: On-Time deliveries have no appeal action → blank status
        return <span className="text-gray-400">-</span>;
      } else {
        label = 'Not Appealed';
      }
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>
        {label}
      </span>
    );
  };

  const getOntimeStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; class: string }> = {
      on_time: { label: 'On Time', class: 'bg-green-100 text-green-800' },
      early: { label: 'Early', class: 'bg-blue-100 text-blue-800' },
      late: { label: 'Late', class: 'bg-red-100 text-red-800' },
    };
    const config = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  if (loading && grns.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading deliveries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchData}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section Tour Trigger */}
      <div className="flex justify-end">
        <SectionTourTrigger
          pageKey="purchasing"
          sectionKey="delivery"
          variant="link"
          label="Tour this tab"
        />
      </div>

      {/* Sub-tabs: Deliveries | Submitted Appeals */}
      <div className="flex gap-2">
        <button
          onClick={() => setMainSubTab('deliveries')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            mainSubTab === 'deliveries'
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Deliveries
        </button>
        <button
          onClick={() => setMainSubTab('appeals')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            mainSubTab === 'appeals'
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Submitted Appeals
        </button>
      </div>

      {mainSubTab === 'deliveries' && (<>
      {/* Filter Section */}
      {showFilters && (
        <DeliveryFilterSection
          filters={filters}
          textInputs={textInputs}
          siteOptions={siteOptions}
          supplierGroupOptions={supplierGroupOptions}
          commodityGroupOptions={commodityGroupOptions}
          commodityTypeOptions={commodityTypeOptions}
          supplierOptions={supplierOptions}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          onSearch={() => fetchData()}
          onHideFilters={() => setShowFilters(false)}
        />
      )}

      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-rose-600" />
              <h3 className="font-semibold">Deliveries</h3>
              <span className="text-sm text-muted-foreground">
                {totalCount} {totalCount === 1 ? 'delivery' : 'deliveries'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                  showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                }`}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
              {/* CR-3861: Export button */}
              <button
                onClick={handleExport}
                disabled={exporting}
                className="px-3 py-1.5 text-sm rounded-lg border hover:bg-muted transition-colors flex items-center gap-1 disabled:opacity-50"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList>
                <TabsTrigger value="all">
                  All{tabCounts['all'] !== undefined ? ` (${tabCounts['all'].toLocaleString()})` : ''}
                </TabsTrigger>
                {/* CR-4651: Exceptions tab — early/late deliveries still needing customer action.
                    CR-5884: respects supplier-side hide_exceptions toggle. */}
                {!hideExceptionsForSupplier && (
                  <TabsTrigger value="exceptions" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                    Exceptions{tabCounts['exceptions'] !== undefined ? ` (${tabCounts['exceptions'].toLocaleString()})` : ''}
                  </TabsTrigger>
                )}
                {/* Early/Late and On Time filter by GRN-level on_time_status,
                    which is undefined on schedule-aware tenants (see the
                    column-hide above). Exceptions stays because it also
                    narrows on workflow_status=pending_review and is the
                    entry point for the customer acknowledge/appeal flow. */}
                {!scheduleCollabActive && (
                  <TabsTrigger value="early_late">
                    Early/Late{tabCounts['early_late'] !== undefined ? ` (${tabCounts['early_late'].toLocaleString()})` : ''}
                  </TabsTrigger>
                )}
                {!scheduleCollabActive && (
                  <TabsTrigger value="on_time">
                    On Time{tabCounts['on_time'] !== undefined ? ` (${tabCounts['on_time'].toLocaleString()})` : ''}
                  </TabsTrigger>
                )}
                <TabsTrigger value="delivered_this_week">
                  Delivered This Week{tabCounts['delivered_this_week'] !== undefined ? ` (${tabCounts['delivered_this_week'].toLocaleString()})` : ''}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Deliveries Table - 25 columns matching v1.89 */}
      <Card data-tour="grn-table">
        <CardContent className="p-0">
          {grns.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No deliveries found matching your criteria.
            </div>
          ) : (
            <>
              {/* Top Pagination */}
              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="deliveries"
                />
              </div>

              {/* CR-3866: Added max-height and sticky header support */}
              <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)]">
                <table className="w-full text-sm min-w-[3000px]">
                  <thead className="sticky top-0 z-20">
                    <tr className="border-b bg-muted/80 backdrop-blur-sm">
                      <th className="text-left py-3 px-4 font-medium sticky left-0 bg-muted z-30 min-w-[140px]">GRN Number</th>
                      <th className="text-left py-3 px-4 font-medium min-w-[80px]">View</th>
                      {/* CR-5884: Action column hidden for supplier when admin sets hide_actions. */}
                      {!hideActionsForSupplier && (
                        <th className="text-left py-3 px-4 font-medium min-w-[130px]">Action</th>
                      )}
                      {!scheduleCollabActive && (
                        <SortableHeader field="on_time_status" label="On Time Status" className="text-left min-w-[140px]" />
                      )}
                      <SortableHeader field="appeal_status" label="Appeal Status" className="text-left min-w-[140px]" />
                      <SortableHeader field="customer__name" label="Customer" className="text-left min-w-[180px]" />
                      <SortableHeader field="po_line__po__supplier__supplier_group__name" label="Supplier Group" className="text-left min-w-[160px]" />
                      <SortableHeader field="po_line__po__supplier__name" label="Supplier" className="text-left min-w-[180px]" />
                      <SortableHeader field="po_line__po__po_number" label="PO Number" className="text-right min-w-[130px]" />
                      <SortableHeader field="po_line__line_number" label="Line Number" className="text-right min-w-[110px]" />
                      <SortableHeader field="po_line__status" label="PO Line Status" className="text-left min-w-[130px]" />
                      <SortableHeader field="po_line__part_number" label="Part Number" className="text-left min-w-[140px]" />
                      <th className="text-left py-3 px-4 font-medium min-w-[200px]">Part Description</th>
                      <SortableHeader field="po_line__quantity" label="Qty Ordered" className="text-right min-w-[120px]" />
                      <SortableHeader field="quantity_received" label="Qty Received" className="text-right min-w-[130px]" />
                      <SortableHeader field="unit_price" label="Unit Price" className="text-right min-w-[120px]" />
                      <SortableHeader field="po_line__ppv" label="PPV" className="text-left min-w-[100px]" />
                      <SortableHeader field="currency" label="Currency" className="text-right min-w-[90px]" />
                      <SortableHeader field="total_value" label="Total Value" className="text-right min-w-[130px]" />
                      {!isSupplier && <SortableHeader field="unit_price_converted" label="Unit Price (Converted)" className="text-right min-w-[160px]" />}
                      {!isSupplier && <SortableHeader field="total_value_converted" label="Total Value (Converted)" className="text-right min-w-[170px]" />}
                      <SortableHeader field="release_note_number" label="Release Note Number" className="text-right min-w-[150px]" />
                      <SortableHeader field="mrp_date" label="MRP Date" className="text-left min-w-[120px]" />
                      <SortableHeader field="review_date" label="Review Date" className="text-left min-w-[130px]" />
                      <SortableHeader field="original_delivery_date" label="ODD" className="text-left min-w-[120px]" />
                      <SortableHeader field="receipt_date" label="Delivery Date" className="text-left min-w-[130px]" />
                    </tr>
                  </thead>
                  <tbody>
                    {/* One row per GoodsReceiptNote record (= one per PO
                        line within a physical delivery note). Per-schedule
                        allocation detail lives in the modal's Schedule
                        Allocation tab. */}
                    {grns.map((grn: GoodsReceiptNote) => {
                      const allocCount = grn.allocations?.length ?? 0;
                      return (
                      <tr
                        key={String(grn.id)}
                        onClick={() => handleViewDetails(grn)}
                        className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <td className="py-3 px-4 font-medium sticky left-0 bg-background z-10">{grn.grn_number}</td>
                        {/* CR-4655: Icon-only View button */}
                        <td className="py-3 px-4">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewDetails(grn); }}
                            className="p-1.5 hover:bg-blue-50 rounded transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4.5 w-4.5 text-blue-600" />
                          </button>
                        </td>
                        {/* CR-4655 + CR-4660: Icon-only Action buttons, Acknowledge opens direct modal */}
                        {/* CR-4651: On-Time deliveries have no action available */}
                        {/* CR-5884: Whole Action column hidden for supplier when hide_actions is on. */}
                        {!hideActionsForSupplier && (
                        <td className="py-3 px-4">
                          {/* CR-4786: Customer — supplier appeal is waiting for a response.
                              The available actions are Accept / Query Supplier / Reject, each
                              opening the Respond-to-Appeal modal. Takes precedence over the
                              Acknowledge/Appeal branch below, which was firing even after a
                              supplier raised an appeal — leaving the customer with the wrong
                              controls (re-acknowledge / re-appeal instead of respond). */}
                          {!isSupplier
                              && grn.latest_appeal
                              && grn.latest_appeal.status === 'awaiting_response'
                              && !grn.latest_appeal.is_customer_appeal ? (
                            <div className="flex gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); openAppealResponseForGrn(grn); }}
                                className="p-1.5 hover:bg-green-50 rounded transition-colors"
                                title="Accept appeal"
                              >
                                <CheckCircle className="h-5 w-5 text-green-600" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); openAppealResponseForGrn(grn); }}
                                className="p-1.5 hover:bg-yellow-50 rounded transition-colors"
                                title="Query supplier"
                              >
                                <HelpCircle className="h-5 w-5 text-yellow-600" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); openAppealResponseForGrn(grn); }}
                                className="p-1.5 hover:bg-red-50 rounded transition-colors"
                                title="Reject appeal"
                              >
                                <XCircle className="h-5 w-5 text-red-600" />
                              </button>
                            </div>
                          ) : /* CR-4651: Customer — Acknowledge + Appeal for Early/Late pending.
                              CR-4739 round 2: suppress once the previous
                              appeal has reached a terminal state (rejected,
                              accepted). The workflow_status was staying at
                              pending_review after a rejection, so without
                              this gate Acknowledge/Appeal lingered on
                              resolved rows.
                              CR-4678 round 2: 'cancelled' must NOT gate
                              the buttons off — a cancelled appeal is
                              intentionally withdrawn by the supplier and
                              both sides should be able to restart the
                              Acknowledge/Appeal loop, exactly like a fresh
                              delivery. Only truly-terminal statuses
                              (rejected / accepted) keep the row closed. */
                          !isSupplier
                              && grn.workflow_status === 'pending_review'
                              && grn.on_time_status !== 'on_time'
                              && !(grn.latest_appeal && ['rejected', 'accepted'].includes(grn.latest_appeal.status)) ? (
                            <div className="flex gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); setAcknowledgeGrn(grn); }}
                                className="p-1.5 hover:bg-green-50 rounded transition-colors"
                                title="Acknowledge"
                              >
                                <Check className="h-5 w-5 text-green-600" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setAppealGrn(grn); }}
                                className="p-1.5 hover:bg-orange-50 rounded transition-colors"
                                title="Appeal"
                              >
                                <AlertTriangle className="h-5 w-5 text-orange-500" />
                              </button>
                            </div>
                          ) : /* CR-4869: Supplier — appeal awaiting customer response.
                              Replace the green Acknowledge tick (which the
                              pending_review branch below was still showing
                              even after the supplier raised an appeal) with
                              a single grey Cancel Appeal X. The supplier
                              can only cancel their OWN appeals, so
                              customer-raised appeals (is_customer_appeal)
                              fall through to the other branches.
                              CR-5884: Cancel-Appeal hidden when admin
                              enables Hide OTIF Override Button. */
                          isSupplier
                              && !hideAppealForSupplier
                              && grn.latest_appeal
                              && grn.latest_appeal.status === 'awaiting_response'
                              && !grn.latest_appeal.is_customer_appeal ? (
                            <button
                              onClick={(e) => handleCancelAppeal(grn, e)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                              title="Cancel Appeal"
                            >
                              <XCircle className="h-5 w-5 text-gray-500" />
                            </button>
                          ) : /* CR-4601 + CR-4651: Supplier — Acknowledge + Appeal icons (hide on on-time).
                              CR-4739 round 2: also suppress when the
                              previous appeal is terminal so the rejected
                              / accepted state doesn't keep offering
                              re-Acknowledge / re-Appeal.
                              CR-4678 round 2: 'cancelled' is intentionally
                              NOT in this list — the supplier cancelled
                              their own appeal and should be able to raise
                              a fresh one. */
                          isSupplier
                              && grn.workflow_status === 'pending_review'
                              && grn.on_time_status !== 'on_time'
                              && !(grn.latest_appeal && ['rejected', 'accepted'].includes(grn.latest_appeal.status)) ? (
                            <div className="flex gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); setAcknowledgeGrn(grn); }}
                                className="p-1.5 hover:bg-green-50 rounded transition-colors"
                                title="Acknowledge"
                              >
                                <Check className="h-5 w-5 text-green-600" />
                              </button>
                              {/* CR-5884: Appeal button hidden for supplier when admin sets Hide OTIF Override Button. */}
                              {grn.can_appeal && !hideAppealForSupplier && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setAppealGrn(grn); }}
                                  className="p-1.5 hover:bg-orange-50 rounded transition-colors"
                                  title="Appeal"
                                >
                                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                                </button>
                              )}
                            </div>
                          ) : /* CR-4601 rework: Supplier — acknowledged state */
                          isSupplier && grn.workflow_status === 'acknowledged' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3" />
                              Acknowledged
                            </span>
                          ) : /* Supplier: Update queried appeal.
                              CR-5884: Hidden when admin sets Hide OTIF Override Button. */
                          isSupplier && !hideAppealForSupplier && grn.latest_appeal?.status === 'queried' ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleViewDetails(grn); }}
                              className="p-1.5 hover:bg-yellow-50 rounded transition-colors"
                              title="Update Appeal"
                            >
                              <AlertTriangle className="h-5 w-5 text-yellow-500" />
                            </button>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        )}
                        {!scheduleCollabActive && (
                          <td className="py-3 px-4">{getOntimeStatusBadge(grn.on_time_status || '')}</td>
                        )}
                        <td className="py-3 px-4">{getAppealStatusBadge(grn.latest_appeal?.status || 'none', grn.latest_appeal?.is_customer_appeal, grn.workflow_status, grn.on_time_status)}</td>
                        <td className="py-3 px-4">{grn.customer_details?.name || grn.customer_name || '-'}</td>
                        <td className="py-3 px-4">{grn.supplier_group || '-'}</td>
                        <td className="py-3 px-4">
                          <span className="text-primary">{grn.supplier_name}</span>
                        </td>
                        <td className="py-3 px-4 text-right font-medium">
                          <a href={`/purchasing?po=${grn.po_number}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{grn.po_number}</a>
                        </td>
                        <td className="py-3 px-4 text-right">{grn.line_number ?? '-'}</td>
                        <td className="py-3 px-4">{grn.po_line_status || '-'}</td>
                        <td className="py-3 px-4 font-mono">{grn.part_number}</td>
                        <td className="py-3 px-4">{grn.part_description || '-'}</td>
                        <td className="py-3 px-4 text-right">{formatQuantity(grn.quantity_ordered || 0)}</td>
                        <td className="py-3 px-4 text-right">
                          {formatQuantity(Number(grn.quantity_received))}
                          {allocCount > 0 && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 align-middle" title="Click to see schedule allocation breakdown">{allocCount} alloc</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">{parseFloat(grn.unit_price || '0').toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                        <td className="py-3 px-4">{(() => {
                          const ppvNum = grn.ppv ? parseFloat(grn.ppv) : null;
                          if (ppvNum === null || isNaN(ppvNum)) return '-';
                          const badge = ppvNum > 0
                            ? 'bg-red-100 text-red-800'
                            : ppvNum < 0
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800';
                          return (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge}`}>
                              {ppvNum.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                          );
                        })()}</td>
                        <td className="py-3 px-4 text-right">{grn.currency || '-'}</td>
                        <td className="py-3 px-4 text-right">{Number(grn.total_value || 0).toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                        {!isSupplier && <td className="py-3 px-4 text-right">{parseFloat(grn.unit_price_converted || '0').toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>}
                        {!isSupplier && <td className="py-3 px-4 text-right">{parseFloat(grn.total_value_converted || '0').toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>}
                        <td className="py-3 px-4 text-right">{grn.release_note_number || '-'}</td>
                        <td className="py-3 px-4">{formatDate(grn.mrp_date)}</td>
                        <td className="py-3 px-4">{formatDate(grn.review_date)}</td>
                        {/* ODD = the attributed schedule's schedule_date
                            (what on_time_status is computed against). The PO
                            header's original_delivery_date is a stale pre-
                            forecast value in schedule-collaboration mode and
                            made the column disagree with the modal / OTD
                            column. Line-0 attributions have no target date
                            so render '-'. */}
                        <td className="py-3 px-4">{grn.schedule_date ? formatDate(grn.schedule_date) : '-'}</td>
                        <td className="py-3 px-4">{formatDate(grn.receipt_date)}</td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Bottom Pagination */}
              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="deliveries"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
      </>)}

      {mainSubTab === 'appeals' && (
        <AppealsSubTab
          formatDate={formatDate}
          getAppealStatusBadge={getAppealStatusBadge}
          refreshKey={appealsRefreshKey}
          onViewGrn={async (grnId) => {
            if (!grnId) {
              toast.error('No GRN ID on this appeal');
              return;
            }
            try {
              const res = await purchasingAPI.getGRN(grnId);
              setSelectedGrn(res.data);
              setShowModal(true);
            } catch (err: unknown) {
              console.error('Failed to fetch GRN:', err);
              toast.error('Failed to load delivery details');
            }
          }}
          onRespondAppeal={async (appealId) => {
            // CR-4651 rework P7: re-fetch the appeal so the response modal
            // gets the freshest copy (status may have changed since the row
            // was rendered).
            try {
              const res = await purchasingAPI.getDeliveryAppeal(appealId);
              setRespondAppeal(res.data);
            } catch (err: unknown) {
              toast.error(getApiErrorMessage(err, 'Failed to load appeal details'));
            }
          }}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {/* Delivery Details Modal */}
      {showModal && selectedGrn && (
        <DeliveryDetailsModal
          grn={selectedGrn}
          onClose={() => {
            setShowModal(false);
            setSelectedGrn(null);
          }}
          onRefresh={() => {
            fetchData();
            // Mutations inside the modal (raise/respond appeal, dispute,
            // acknowledge) all touch appeal state, so re-pull the appeals
            // subtab data too — otherwise switching to that tab afterwards
            // shows stale rows until F5.
            setAppealsRefreshKey(k => k + 1);
          }}
        />
      )}
      {/* CR-4602: Direct appeal modal from Action column */}
      {appealGrn && (
        <RaiseAppealModal
          isOpen={true}
          grn={appealGrn}
          onClose={() => setAppealGrn(null)}
          onSuccess={() => {
            setAppealGrn(null);
            fetchData();
            setAppealsRefreshKey(k => k + 1);
          }}
        />
      )}
      {/* CR-4660: Direct acknowledge modal (skips details popup) */}
      {acknowledgeGrn && (
        <AcknowledgeGRNModal
          isOpen={true}
          onClose={() => setAcknowledgeGrn(null)}
          grnId={acknowledgeGrn.id}
          grnNumber={acknowledgeGrn.grn_number}
          partNumber={acknowledgeGrn.part_number || ''}
          poNumber={acknowledgeGrn.po_number || ''}
          onSubmit={handleAcknowledgeSubmit}
        />
      )}

      {/* CR-4786: Respond-to-appeal modal for customer replies to supplier appeals */}
      {respondAppeal && (
        <AppealResponseModal
          isOpen={true}
          onClose={() => setRespondAppeal(null)}
          appeal={respondAppeal}
          onSuccess={(action) => {
            // Optimistic patch so the row's badge/buttons update immediately,
            // before the network refetch lands. action -> appeal status:
            // accept -> accepted, query -> queried, reject -> rejected.
            const nextStatus: Record<'accept' | 'query' | 'reject', 'accepted' | 'queried' | 'rejected'> = {
              accept: 'accepted',
              query: 'queried',
              reject: 'rejected',
            };
            const appealId = respondAppeal.id;
            const newStatus = nextStatus[action];
            setGrns(prev => prev.map(g => {
              if (!g.latest_appeal || g.latest_appeal.id !== appealId) return g;
              return {
                ...g,
                latest_appeal: { ...g.latest_appeal, status: newStatus },
              };
            }));
            setRespondAppeal(null);
            fetchData();
            // Force the appeals subtab to re-pull its list + counts as well —
            // its state lives inside AppealsSubTab so fetchData() doesn't reach it.
            setAppealsRefreshKey(k => k + 1);
          }}
        />
      )}
    </div>
  );
}
